// The chosen sample and what the browser learned about it before upload (DESIGN_SPEC §11.2 flow states).
//   usePickFile(cols) → { state, pick(file), clear(), open(), inputRef, onInputChange }
//     state: idle · hashing { file, progress 0–1 } · picked { file, analysis } · rejected { file, reason } · failed { file, message }
//     cols: the portrait's column count for the current plate width; a change redraws a picked portrait (hash kept)
//     open() clicks the hidden <input type=file>; picking the same file again works (the input is reset)
//     the hashing and the portrait run in a worker (analyzeClient), so a large sample never stalls the page
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { MAX_SAMPLE_BYTES, type Analysis } from "./analyze";
import { runAnalyze, runPortrait } from "./analyzeClient";

export type PickState =
  | { status: "idle" }
  | { status: "hashing"; file: File; progress: number }
  | { status: "picked"; file: File; analysis: Analysis }
  | { status: "rejected"; file: File; reason: "empty" | "too-large" }
  | { status: "failed"; file: File; message: string };

const isAbort = (e: unknown) => e instanceof Error && e.name === "AbortError";

export function usePickFile(cols: number) {
  const [state, setState] = useState<PickState>({ status: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);
  const run = useRef<AbortController | null>(null);
  const colsRef = useRef(cols);
  useEffect(() => {
    colsRef.current = cols;
  }, [cols]);
  const frame = useRef(0);

  const stop = () => {
    run.current?.abort();
    run.current = null;
    cancelAnimationFrame(frame.current);
  };

  useEffect(() => stop, []);

  const pick = useCallback((file: File) => {
    stop();
    if (file.size === 0) return setState({ status: "rejected", file, reason: "empty" });
    if (file.size > MAX_SAMPLE_BYTES) return setState({ status: "rejected", file, reason: "too-large" });
    const ac = new AbortController();
    run.current = ac;
    setState({ status: "hashing", file, progress: 0 });
    let latest = 0;
    const onProgress = (p: number) => {
      latest = p;
      if (frame.current) return;
      // one state update per frame, however often the pass reports
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        if (!ac.signal.aborted) setState((s) => (s.status === "hashing" && s.file === file ? { ...s, progress: latest } : s));
      });
    };
    runAnalyze(file, colsRef.current, { signal: ac.signal, onProgress }).then(
      (analysis) => {
        if (ac.signal.aborted) return;
        cancelAnimationFrame(frame.current);
        frame.current = 0;
        run.current = null;
        setState({ status: "picked", file, analysis });
      },
      (err: unknown) => {
        if (ac.signal.aborted || isAbort(err)) return;
        run.current = null;
        const name = err instanceof Error ? err.name : "";
        setState({ status: "failed", file, message: name === "NotReadableError" ? "the browser could not read it (moved or changed since it was chosen?)" : String((err as Error)?.message ?? err) });
      },
    );
  }, []);

  // a plate that crosses the 240/120-column threshold redraws the portrait; the hash and type stay
  const pickedFile = state.status === "picked" ? state.file : null;
  const pickedCols = state.status === "picked" ? state.analysis.portrait.grid.cols : cols;
  useEffect(() => {
    if (!pickedFile || pickedCols === cols) return;
    const ac = new AbortController();
    runPortrait(pickedFile, cols, { signal: ac.signal }).then(
      (portrait) => {
        if (!ac.signal.aborted) setState((s) => (s.status === "picked" && s.file === pickedFile ? { ...s, analysis: { ...s.analysis, portrait } } : s));
      },
      () => undefined,
    );
    return () => ac.abort();
  }, [pickedFile, pickedCols, cols]);

  const clear = useCallback(() => {
    stop();
    setState({ status: "idle" });
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const open = useCallback(() => inputRef.current?.click(), []);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) pick(file);
      e.target.value = ""; // the same file can be chosen again
    },
    [pick],
  );

  return { state, pick, clear, open, inputRef, onInputChange };
}
